/**
 * J8 auto-quest "ต่อไป" (docs/design/2026-10-11-auto-play.md): one press walks to the current quest step (nav.ts
 * `navTo` quest, which mounts by itself), talks to accept and to turn in, and carries on to the next step. It HANDS
 * OVER at fights (a notice, then it stops) unless auto-battle is on (World.setAuto only switches on where autoRules allows it), and at any step that
 * is not a walk and a word (meditate, duels, sect …). Any control of the player's own ends it (nav's `input` stop).
 * Time only via `step(dt)`; no randomness.
 */
import type { PlayerCommand, WorldEvent } from '../world';
import { navOf, type NavTarget } from './nav';
import { npcMark } from './plainsStory';
import type { SystemHost, WorldSystem } from './types';

export type AutoQuestCommand = { t: 'autoQuest'; on: boolean };

export const AUTO_QUEST_NOTICE = {
  fight: 'ถึงด่านต่อสู้ ต้องสู้เอง (ต่อไป หยุดแล้ว)',
  manual: 'ขั้นนี้ต้องทำเอง (ต่อไป หยุดแล้ว)',
  none: 'ไม่มีขั้นภารกิจให้ไปต่อ',
  done: 'ต่อไป: ถึงขั้นต่อไปแล้ว',
} as const;

/** Seconds between looks; failed starts in a row before it gives up. */
const THINK = 0.5;
const MAX_FAILS = 3;
/** Objective types that are only a walk (a place or a person): the rest are the player's. */
/** Objective types that are fights (hand over, or auto-battle when it is on). */
const FIGHTS = new Set(['kill', 'lockForm', 'block', 'guardBreak', 'interrupt', 'duelWin']);
const WALKED = new Set(['talk', 'visit']);

const SYSTEMS = new WeakMap<object, AutoQuestSystem>();
export const autoQuestOf = (w: object): AutoQuestSystem | undefined => SYSTEMS.get(w);

export class AutoQuestSystem implements WorldSystem {
  readonly name = 'autoQuest';
  on = false;
  private t = 0;
  private fails = 0;
  private pending: 'accept' | 'turnIn' | null = null;
  private waitT = 0;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  readonly commands = {
    autoQuest: (cmd: { on: boolean }) => {
      if (cmd.on === this.on) return;
      this.on = cmd.on;
      this.t = 0;
      this.fails = 0;
      this.pending = null;
      this.waitT = 0;
      if (!cmd.on) {
        if (navOf(this.h.world)?.state) this.h.world.apply({ t: 'navStop' });
      }
    },
  };

  observe(e: WorldEvent): void {
    if (!this.on) return;
    if (e.type === 'dialog' && (e.action === 'accept' || e.action === 'turnIn')) this.pending = e.action;
  }

  private off(text?: string): void {
    this.on = false;
    if (text) this.h.notice(text);
  }

  step(dt: number): void {
    if (!this.on) return;
    const w = this.h.world;
    if (w.player.dead) return this.off();
    if (w.scene) return;
    // The talk answered: accept or hand in.
    if (this.pending) {
      const act: PlayerCommand = this.pending === 'accept' ? { t: 'questAccept' } : { t: 'questTurnIn' };
      this.pending = null;
      w.apply(act);
      this.t = THINK;
      this.waitT = 0;
      return;
    }
    this.t -= dt;
    if (this.t > 0) return;
    this.t = THINK;
    const nav = navOf(w);
    if (!nav) return this.off();
    if (nav.state) return;
    // Right after a walk ends the talk's dialog arrives within a moment: give it one look.
    if (this.waitT < THINK && this.fails === 0 && this.walked) {
      this.waitT += THINK;
      return;
    }
    const q = w.currentQuest();
    if (!q) return this.off(AUTO_QUEST_NOTICE.none);
    const qs = w.progress.quest;
    if (qs.status === 'active') {
      const o = q.objectives.find((ob, k) => (qs.progress[k] ?? 0) < ob.count);
      if (o && FIGHTS.has(o.type)) {
        if (w.auto.on) return;
        return this.off(AUTO_QUEST_NOTICE.fight);
      }
      if (o && !WALKED.has(o.type)) return this.off(AUTO_QUEST_NOTICE.manual);
    }
    let target: NavTarget = { kind: 'quest' };
    // A giver other than the elder wears the quest mark (plains story): walk to them.
    if (qs.status !== 'active')
      for (const e of w.entities.values()) {
        if (e.kind !== 'npc' || e === w.elder) continue;
        const m = npcMark(w, e);
        if (m === 'offer' || m === 'ready') {
          target = { kind: 'npc', id: e.id };
          break;
        }
      }
    w.apply({ t: 'navTo', target });
    this.walked = !!nav.state;
    this.waitT = 0;
    if (!nav.state) {
      if (++this.fails >= MAX_FAILS) this.off();
    } else this.fails = 0;
  }

  private walked = false;
}
